import { currentUserQuery } from "@common/api"
import { LoadingState } from "@common/components/shared/loading-state"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@common/components/ui/sidebar"
import { TraceProvider } from "@common/hooks/trace-provider"
import useAuth, { isLoggedIn } from "@common/hooks/use-auth"
import { useMaxWidth } from "@common/hooks/use-mobile"
import { useNotificationSocket } from "@common/hooks/use-notification-socket"
import { useUIState } from "@common/hooks/use-ui-state"
import { sanitizePostHogRoute } from "@common/lib/posthog"
import { usePostHog } from "@posthog/react"
import {
  createFileRoute,
  Outlet,
  redirect,
  useMatches,
  useRouterState,
} from "@tanstack/react-router"
import { AxiosError } from "axios"
import posthogClient from "posthog-js"
import { Suspense, useEffect } from "react"
import { AppSidebar } from "@/components/layout/app-sidebar"
import {
  ChatPanel,
  ChatPanelTrigger,
  ChatReturnLink,
} from "@/components/layout/chat-panel"
import { ContinueTraceFromParam } from "@/components/layout/continue-trace-from-param"
import { InboxPanel } from "@/components/layout/inbox-panel"
import { LayoutBreadcrumbs } from "@/components/layout/layout-breadcrumbs"
import { NavBar } from "@/components/layout/nav-bar"
import {
  SecondaryPanelProvider,
  SecondaryPanelSlot,
} from "@/components/layout/secondary-panel"
import { ChatPanelProvider, useChatPanel } from "@/hooks/use-chat-panel"
import { InboxPanelProvider } from "@/hooks/use-inbox-panel"
import { NavBarProvider } from "@/hooks/use-nav-bar"
import { useSentryUser } from "@/hooks/use-sentry-user"
import { isTraceAgentPath } from "@/lib/nav-links"

export const Route = createFileRoute("/_layout")({
  component: Layout,
  beforeLoad: async ({ context: { queryClient }, location }) => {
    if (!isLoggedIn()) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      })
    }

    const user = await queryClient
      .fetchQuery(currentUserQuery())
      .catch((error: unknown) => {
        const status =
          error instanceof AxiosError ? error.response?.status : undefined
        if (status === 401 || status === 403) {
          localStorage.removeItem("access_token")
          throw redirect({
            to: "/login",
            search: { redirect: location.href },
          })
        }
        throw error
      })

    if (user.must_change_password) {
      throw redirect({ to: "/change-password" })
    }

    const previousTenant = posthogClient.get_property("tenant_id")
    posthogClient.register({
      tenant_id: user.tenant.id,
      tenant_slug: user.tenant.slug,
      is_internal: user.is_internal || user.tenant.is_internal,
    })
    if (previousTenant !== user.tenant.id) {
      posthogClient.capture("$pageview", {
        tenant_id: user.tenant.id,
        tenant_slug: user.tenant.slug,
        route: sanitizePostHogRoute(location.pathname),
        $current_url: window.location.origin + location.href,
      })
    }
  },
})

function Layout() {
  const { isLoading, user } = useAuth()
  useSentryUser(user)
  useNotificationSocket()
  const posthog = usePostHog()
  const getUIState = useUIState()

  const showChatTrigger = useRouterState({
    select: (s) =>
      s.location.pathname !== "/" && !isTraceAgentPath(s.location.pathname),
  })

  const fromTraceId = useRouterState({
    select: (s) => s.location.search.from_trace_id,
  })

  useEffect(() => {
    if (user) {
      posthog.identify(user?.id, {
        tenantId: user.tenant.id,
        tenantSlug: user.tenant.slug,
        isInternal: user.is_internal || user.tenant.is_internal,
        name: `${user.first_name} ${user.last_name?.charAt(0)}.`,
        webCommitSha: import.meta.env.VITE_GIT_COMMIT_SHA,
      })
      posthog.group("company", `${user.tenant.id}`, {
        name: user.tenant.name,
        slug: user.tenant.slug,
        is_internal: user.tenant.is_internal,
      })
      posthog.startSessionRecording()
    } else {
      posthog.stopSessionRecording()
      posthog.reset()
    }
    return () => {
      posthog.stopSessionRecording()
    }
  }, [user, posthog])

  return (
    <InboxPanelProvider>
      <ChatPanelProvider>
        <NavBarProvider>
          <TraceProvider getUIState={getUIState} fromTraceId={fromTraceId}>
            <ContinueTraceFromParam />
            <AdaptiveSidebarProvider>
              <SecondaryPanelProvider>
                <AppSidebar />
                <SecondaryPanelSlot />
                <SidebarInset className="relative max-h-svh overflow-hidden md:peer-data-[variant=inset]:max-h-[calc(100svh-1rem)]">
                  <div className="flex h-10 shrink-0 items-center gap-2 border-b px-3">
                    <SidebarTrigger />
                    <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
                      <div className="min-w-0 flex-1">
                        <LayoutBreadcrumbs />
                      </div>
                    </div>
                    {showChatTrigger && (
                      <ChatPanelTrigger className="ml-auto" />
                    )}
                    <ChatReturnLink />
                  </div>
                  <div className="flex min-h-0 flex-1">
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                      <NavBar />
                      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hide">
                        {isLoading ? (
                          <LoadingState />
                        ) : (
                          <Suspense fallback={<LoadingState />}>
                            <Outlet />
                          </Suspense>
                        )}
                      </div>
                    </div>
                    <ChatPanel />
                  </div>
                  <InboxPanel />
                </SidebarInset>
              </SecondaryPanelProvider>
            </AdaptiveSidebarProvider>
          </TraceProvider>
        </NavBarProvider>
      </ChatPanelProvider>
    </InboxPanelProvider>
  )
}

const SIDEBAR_OVERLAY_BREAKPOINT = 1280

function AdaptiveSidebarProvider({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  const { isOpen: chatOpen } = useChatPanel()
  const compact = useMaxWidth(SIDEBAR_OVERLAY_BREAKPOINT)
  const hasSecondarySidebar = useMatches({
    select: (matches) =>
      matches.some((match) => match.staticData.secondarySidebar),
  })
  const hasDetailSidebar = useMatches({
    select: (matches) =>
      matches.some((match) => match.staticData.detailSidebar),
  })
  const rightPanelOpen = chatOpen || hasDetailSidebar
  const allSidebarsOpen = chatOpen && hasSecondarySidebar && hasDetailSidebar

  return (
    <SidebarProvider
      forceOverlay={(rightPanelOpen && compact) || allSidebarsOpen}
    >
      {children}
    </SidebarProvider>
  )
}
